import React, { useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { adminApi, FitReference } from '../services/adminApi';
import { useToast } from '../contexts/ToastContext';
import './AdminFitReferences.css';

// A card being edited; new cards have no id until saved
type DraftFitReference = Omit<FitReference, 'id'> & { id?: number; key: string };

const toDraft = (ref: FitReference): DraftFitReference => ({ ...ref, key: `ref-${ref.id}` });

const AdminFitReferences: React.FC = () => {
  const { showToast } = useToast();
  const [references, setReferences] = useState<DraftFitReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    adminApi.getFitReferences()
      .then(refs => setReferences(refs.map(toDraft)))
      .catch(() => showToast('Failed to load fit references', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const updateReference = (key: string, changes: Partial<DraftFitReference>) => {
    setReferences(prev => prev.map(ref => (ref.key === key ? { ...ref, ...changes } : ref)));
  };

  const updateMeasurement = (ref: DraftFitReference, index: number, field: 'label' | 'value', value: string) => {
    updateReference(ref.key, {
      measurements: ref.measurements.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    });
  };

  const addReference = () => {
    setReferences(prev => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        name: '',
        type: 'dressform',
        measurements: [
          { label: 'Bust', value: '' },
          { label: 'Waist', value: '' },
          { label: 'Hips', value: '' }
        ]
      }
    ]);
  };

  const saveReference = async (ref: DraftFitReference) => {
    if (!ref.name.trim()) {
      showToast('Please give the fit reference a name', 'error');
      return;
    }
    setSavingKey(ref.key);
    try {
      const { key, ...payload } = ref;
      const saved = await adminApi.saveFitReference(payload);
      setReferences(prev => prev.map(r => (r.key === key ? toDraft(saved) : r)));
      showToast('Fit reference saved');
    } catch (error: any) {
      showToast(error.message || 'Failed to save fit reference', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const deleteReference = async (ref: DraftFitReference) => {
    if (ref.id === undefined) {
      setReferences(prev => prev.filter(r => r.key !== ref.key));
      return;
    }
    if (!window.confirm(`Delete "${ref.name}"? Products using it will no longer show a fit reference.`)) return;
    try {
      await adminApi.deleteFitReference(ref.id);
      setReferences(prev => prev.filter(r => r.key !== ref.key));
      showToast('Fit reference deleted');
    } catch (error: any) {
      showToast(error.message || 'Failed to delete fit reference', 'error');
    }
  };

  return (
    <AdminLayout>
      <div className="admin-fit-references">
        <div className="fitref-header">
          <div>
            <h1>Fit Reference</h1>
            <p>Dressforms and models your products are displayed on. Pick one per product in Add / Edit Product.</p>
          </div>
          <button className="fitref-btn fitref-btn-primary" onClick={addReference}>
            ➕ Add Fit Reference
          </button>
        </div>

        {loading ? (
          <p className="fitref-empty">Loading...</p>
        ) : references.length === 0 ? (
          <p className="fitref-empty">No fit references yet. Add your first dressform or model.</p>
        ) : (
          <div className="fitref-grid">
            {references.map(ref => (
              <div key={ref.key} className="fitref-card">
                <div className="fitref-row">
                  <div className="fitref-field">
                    <label>Name</label>
                    <input
                      type="text"
                      value={ref.name}
                      onChange={e => updateReference(ref.key, { name: e.target.value })}
                      placeholder="e.g., White dressform"
                    />
                  </div>
                  <div className="fitref-field fitref-type">
                    <label>Displayed on</label>
                    <select
                      value={ref.type}
                      onChange={e => updateReference(ref.key, { type: e.target.value as FitReference['type'] })}
                    >
                      <option value="dressform">Dressform</option>
                      <option value="model">Model</option>
                    </select>
                  </div>
                </div>

                <label className="fitref-section-label">Measurements</label>
                {ref.measurements.map((m, index) => (
                  <div key={index} className="fitref-measurement">
                    <input
                      type="text"
                      value={m.label}
                      onChange={e => updateMeasurement(ref, index, 'label', e.target.value)}
                      placeholder="Name (e.g., Bust)"
                    />
                    <input
                      type="text"
                      value={m.value}
                      onChange={e => updateMeasurement(ref, index, 'value', e.target.value)}
                      placeholder="Value (e.g., 87 cm)"
                    />
                    <button
                      type="button"
                      className="fitref-remove"
                      onClick={() => updateReference(ref.key, { measurements: ref.measurements.filter((_, i) => i !== index) })}
                      title="Remove measurement"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="fitref-add-measurement"
                  onClick={() => updateReference(ref.key, { measurements: [...ref.measurements, { label: '', value: '' }] })}
                >
                  + Add measurement
                </button>

                <div className="fitref-actions">
                  <button className="fitref-btn fitref-btn-danger" onClick={() => deleteReference(ref)}>
                    Delete
                  </button>
                  <button
                    className="fitref-btn fitref-btn-primary"
                    onClick={() => saveReference(ref)}
                    disabled={savingKey === ref.key}
                  >
                    {savingKey === ref.key ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFitReferences;
